import { Button, Select, Switch, Tooltip } from "antd"
import { RefreshCw } from "lucide-react"
import { useQuery } from "@tanstack/react-query"
import { ENVIRONMENTS, type CompareMode, type Environment } from "@csbi/contracts"
import type { ThemeMode } from "@csbi/viz-core"
import { fetchTenants } from "../../tiles/api.js"
import { FILTER_CONTROL_SIZE, FILTER_SELECT_WIDTH } from "../control-size.js"
import { effectiveGranularity, rangeLabel, useFilterStore } from "../model/filter-store.js"
import { RangePicker } from "./range-picker.js"
import { TenantPicker } from "./tenant-picker.js"

const ENV_LABELS: Record<Environment | "all", string> = {
  all: "All",
  dev: "Dev",
  staging: "Staging",
  production: "Production",
}

const COMPARE_LABELS: Record<CompareMode, string> = {
  overlay: "Overlay",
  smallMultiples: "Small multiples",
  table: "Table",
}

export type GlobalFilterBarProps = {
  mode: ThemeMode
  onRefresh?: () => void
  generatedAt?: string
}

export function GlobalFilterBar({ mode, onRefresh, generatedAt }: GlobalFilterBarProps) {
  const filters = useFilterStore((s) => s.filters)
  const patch = useFilterStore((s) => s.set)

  const { data, isLoading } = useQuery({
    queryKey: ["tenants"],
    queryFn: ({ signal }) => fetchTenants(signal),
    staleTime: 5 * 60_000,
  })
  const tenants = data?.tenants ?? []
  const [env] = filters.environments

  return (
    <div
      data-csbi="filter-bar"
      style={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        /**
         * ⚠ `alignItems` alone does **not** centre anything on a wrapping flex container.
         */
        alignContent: "center",
        gap: 10,
        minHeight: "var(--rd-nav-height, 64px)",
        padding: "10px 24px",
        borderBottom: "1px solid var(--csbi-gridline)",
        background: "var(--csbi-surface)",
      }}
    >
      <Field label="Environment">
        <Select
          size={FILTER_CONTROL_SIZE}
          style={{ width: FILTER_SELECT_WIDTH.environment }}
          value={env ?? "all"}
          options={(["all", ...ENVIRONMENTS] as const).map((v) => ({ label: ENV_LABELS[v], value: v }))}
          onChange={(next) => {
            const value = next as Environment | "all"
            const environments: Environment[] = value === "all" ? [] : [value]
            // Changing environment has to drop selected deployments that do not belong to it, or the filter bar says 6 while the chart shows 2 lines
            const allowed = new Set(
              tenants.filter((t) => !environments.length || environments.includes(t.environment)).map((t) => t.id),
            )
            patch({ environments, tenants: filters.tenants.filter((id) => allowed.has(id)) })
          }}
        />
      </Field>

      <Field label="Customers">
        <TenantPicker
          tenants={tenants}
          value={filters.tenants}
          onChange={(next) => patch({ tenants: next })}
          mode={mode}
          loading={isLoading}
          onSwitchCompareMode={(compareMode) => patch({ compareMode })}
        />
      </Field>

      <Field label="Range">
        <RangePicker value={filters.range} onChange={(range) => patch({ range })} label={rangeLabel(filters)} />
      </Field>

      <Field label="Granularity">
        <Select
          size={FILTER_CONTROL_SIZE}
          style={{ width: FILTER_SELECT_WIDTH.granularity }}
          value={filters.granularity}
          options={[
            { label: `Auto (${effectiveGranularity(filters)})`, value: "auto" },
            { label: "Day", value: "day" },
            { label: "Week", value: "week" },
            { label: "Month", value: "month" },
          ]}
          onChange={(v) => patch({ granularity: v as typeof filters.granularity })}
        />
      </Field>

      <Field label="Compare as">
        <Select
          size={FILTER_CONTROL_SIZE}
          style={{ width: FILTER_SELECT_WIDTH.compare }}
          value={filters.compareMode}
          options={(["overlay", "smallMultiples", "table"] as const).map((v) => ({
            label: COMPARE_LABELS[v],
            value: v,
          }))}
          onChange={(v) => patch({ compareMode: v as CompareMode })}
        />
      </Field>

      <Field label="Previous period">
        <Switch
          size={FILTER_CONTROL_SIZE}
          checked={filters.comparePrevious}
          onChange={(pop) => patch({ comparePrevious: pop })}
          aria-label="Compare with the previous period"
        />
      </Field>

      <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
        {generatedAt && (
          <span style={{ fontSize: 11, color: "var(--csbi-ink-muted)" }}>{generatedAt}</span>
        )}
        {onRefresh && (
          <Tooltip title="Force a refetch (bypasses the cache)">
            <Button size={FILTER_CONTROL_SIZE} aria-label="Refresh" icon={<RefreshCw size={14} />} onClick={onRefresh} />
          </Tooltip>
        )}
      </div>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <span style={{ fontSize: 12, color: "var(--csbi-ink-muted)" }}>{label}</span>
      {children}
    </span>
  )
}
